// answers.js
// Step 6 of a check (Spec 3 section 7.1): a person's answers to a parked
// batch's questions. The questions dialog (components/data/manage/
// QuestionsDialog.js) turns each chosen resolution into updates of the stable
// subjects it affects (resolveChoice), merges every update into one complete
// replacement answer per subject (draftAnswers), asks about the assignments,
// saved or drafted, those replacements disturb (dependentSlots), and submits
// them (submitAnswers). Nothing the browser writes is an answer until the backend
// promotes it (functions/ingestion/walk/promotion.py).
//
// A question document, batches/{bid}/questions/{qid}, belongs to one stable
// subject: an incoming row or an original landed belief source. It holds the
// walk's latest prompt about that subject (kind, identifier, data_profile_id,
// rows, this_row, attributes, relevant_rows, compared_with, look_alikes,
// carried, carried_count, offered, notes), open_at while the prompt is
// unresolved, the accepted answer and, while a submission stages it,
// pending_answer. An answer is
//
//   { relevant_rows: { <evidence key>: <RowEvidence> },   the snapshots its slots are bound to
//     identifier: null | slot, data_profile_id: null | slot,
//     attributes: { <every attribute>: null | slot }, drop: null | slot }
//
//   slot = { value, basis: { question_id, trigger, choice, from, comparison, evidence_keys },
//            reference, included_via, confirmed_profile (identifier slots only) }
//
// Promotion stamps an accepted slot's `decision`; the browser never writes one.
// Slots go in three groups, each assigned and withdrawn whole: the identifier;
// the profile with every attribute (a bundle); the drop.
//
// The same protocol, written in Python for the live tests, is in
// functions/tests/live_tests/support/answers.py; firestore.rules admits exactly
// these writes (batchUpdateAllowed, questionUpdateAllowed, and the finish
// mirror and abort wake of fileUpdateAllowed).

import {
  Timestamp, collection, doc, documentId, getDoc, getDocs, limit, orderBy, query, runTransaction,
  serverTimestamp, startAfter, where,
} from 'firebase/firestore';
import { db, platformAuth } from './firebase';
import { GestureRefused } from './cleaningGestures';
import { tableScope } from '../utils/blobPaths';
import { canonicalJson, evidenceKey } from '../utils/answerKeys';
import { stableStringify } from '../utils/stableJson';

// What a submission writes per transaction, and the limits it budgets for
// (Spec 3 section 7.1): question writes per chunk, the bytes of one commit and
// of one document.
export const ANSWER_WRITE_CHUNK_SIZE = 400;
const COMMIT_BYTES_LIMIT = 9 * 1024 * 1024;
const DOCUMENT_BYTES_LIMIT = 1000000;
// A reservation no staging chunk has refreshed for this long is the reaper's
// to clean up (CLAIM_STALE_SECONDS in common/timeutils.py).
const CLAIM_STALE_SECONDS = 600;
// The open prompts the dialog shows per page, and the records a roster page reads.
export const PROMPT_PAGE_SIZE = 50;
const ROSTER_PAGE_SIZE = 200;
// Cleaning names a row with no identifier `__missing__{unit}__{row}`; no answer may assign one.
export const PLACEHOLDER_PREFIX = '__missing__';
// An answer's slot groups: the identifier, the profile bundle, the drop.
const SLOT_GROUPS = ['identifier', 'data_profile_id', 'drop'];
const ROW_KEY_FIELDS = ['file_id', 'unit_id', 'row', 'period_label', 'page', 'record'];

// The sentences a submission or a choice is refused with (GestureRefused).
export const ANOTHER_SUBMISSION = 'Another user is submitting answers';
const SIGNED_OUT = 'You are signed out. Sign in again to submit your answers.';
const CHECK_GONE = 'This check no longer exists. Reload the file.';
const NOT_TAKING_ANSWERS = 'This check is not taking answers now.';
export const QUESTION_CHANGED = 'A question changed while you were answering it. Read it again, then submit.';
const DEPENDENT_CHANGED = 'A saved answer depends on one you changed. Review it, then submit.';
const ANSWER_TOO_LARGE = 'An answer, with the rows it keeps, is too large to save.';
const NOTHING_CHANGED = 'Choose at least one answer to submit.';
const TYPE_AN_IDENTIFIER = 'Type an identifier.';
const NO_PLACEHOLDER = `An identifier cannot start with '${PLACEHOLDER_PREFIX}'.`;

// ── Reading the questions ──────────────────────────────────────────────────

// One page of a batch's question documents ({ id, ...data }), and the cursor of
// the next page, null after the last. With `openOnly`, the open prompts, by a
// range on open_at alone, which leaves out every supporting or answered record
// whose open_at is null; otherwise every record, in document-id order. Both
// are single-field queries.
export async function pageQuestions(companyId, tableId, batchId, { openOnly = false, after = null } = {}) {
  const questions = collection(db, `${tableScope(companyId, tableId)}/batches/${batchId}/questions`);
  const size = openOnly ? PROMPT_PAGE_SIZE : ROSTER_PAGE_SIZE;
  const constraints = openOnly
    ? [where('open_at', '>', Timestamp.fromMillis(0)), orderBy('open_at')]
    : [orderBy(documentId())];
  if (after !== null) constraints.push(startAfter(after));
  constraints.push(limit(size));
  const snapshot = await getDocs(query(questions, ...constraints));
  return {
    records: snapshot.docs.map((document) => ({ id: document.id, ...document.data() })),
    next: snapshot.docs.length === size ? snapshot.docs[snapshot.docs.length - 1] : null,
  };
}

// Every question record of the batch, by id: the roster a choice's complete
// scope is built from (display pages never limit it), the accepted answers
// whose slots another change may disturb, and, for a submission, the frozen
// records its drafts were made from.
export async function loadRoster(companyId, tableId, batchId) {
  const roster = new Map();
  let after = null;
  do {
    const page = await pageQuestions(companyId, tableId, batchId, { after });
    for (const record of page.records) roster.set(record.id, record);
    after = page.next;
  } while (after !== null);
  return roster;
}

// ── Subjects, references and snapshots ─────────────────────────────────────

// The `ref` a subject's own snapshots carry: an incoming row's six provenance
// fields, or { source: { identifier, data_profile_id } } for a belief source.
export function ownReference(subject) {
  if (subject.kind === 'row') return Object.fromEntries(ROW_KEY_FIELDS.map((field) => [field, subject[field]]));
  return { source: { identifier: subject.identifier, data_profile_id: subject.data_profile_id } };
}

// The subject a reference names: a value reference or a snapshot's `ref` with
// `source` names that original belief source (the displayed identifier and
// profile beside it are not part of it); any other names an incoming row.
export function subjectOfReference(reference) {
  if (Object.hasOwn(reference, 'source')) {
    const { identifier, data_profile_id: profile } = reference.source;
    return { kind: 'belief_source', identifier, data_profile_id: profile };
  }
  return { kind: 'row', ...Object.fromEntries(ROW_KEY_FIELDS.map((field) => [field, reference[field]])) };
}

// The roster's records by their subject's canonical JSON.
export function recordsBySubject(roster) {
  const bySubject = new Map();
  for (const record of roster.values()) bySubject.set(canonicalJson(record.subject), record);
  return bySubject;
}

// The record's own snapshot among its prompt's relevant rows (the subject as
// filed, or as the belief read before this batch's answers), or null.
export function ownEvidence(record) {
  const own = canonicalJson(ownReference(record.subject));
  return record.relevant_rows.find((row) => canonicalJson(row.ref) === own) || null;
}

// The subject's own underlying row, which withdrawing the accepted `slot`
// gives back: its snapshot among the prompt's relevant rows, else the one
// `slot` was decided against, which the answer keeps. A belief source whose
// identifier another profile now holds has no snapshot of its own among its
// evidence_changed prompt's rows, which show the new owner's instead (Spec 3
// section 2.3); its accepted slots still keep its own. As the Python twin's
// own_snapshot (functions/tests/live_tests/support/answers.py).
function ownSnapshot(record, slot) {
  const current = ownEvidence(record);
  if (current !== null) return current;
  const own = canonicalJson(ownReference(record.subject));
  for (const key of slot.basis.evidence_keys) {
    const saved = record.answer.relevant_rows[key];
    if (saved !== undefined && canonicalJson(saved.ref) === own) return saved;
  }
  throw new Error(`The saved answer on question ${record.id} keeps no snapshot of its own subject.`);
}

// How a choice refers to a record it copies from: an incoming row by its six
// fields; a belief source by its original source, with the identifier and
// profile it is displayed under.
function valueReference(record) {
  if (record.subject.kind === 'row') return ownReference(record.subject);
  return {
    source: { identifier: record.subject.identifier, data_profile_id: record.subject.data_profile_id },
    identifier: record.identifier, data_profile_id: record.data_profile_id,
  };
}

// What the person was shown of a record a choice copies from.
function comparisonOf(record) {
  return {
    ...valueReference(record), identifier: record.identifier, data_profile_id: record.data_profile_id,
    details: record.this_row, attributes: record.attributes,
  };
}

// The inclusion provenance of a record a group operation reaches: through its
// own identifier assignment when that is what puts it under `identifier`
// (Spec 3 section 2.1: t1's bundle records inclusion via t1's identifier
// slot), directly otherwise.
function inclusionOf(record, identifier) {
  const assigned = record.answer?.identifier;
  return assigned != null && assigned.value === identifier ? [{ question_id: record.id, field: 'identifier' }] : [];
}

// A slot as the person saved it, without the decision promotion stamped.
function withoutDecision(slot) {
  if (slot === null) return null;
  const { decision, ...saved } = slot;
  return saved;
}

// The answer a submission starts a subject's replacement from: its accepted
// slots as saved, with their snapshots, or an empty map.
export function replacementAnswer(record) {
  const accepted = record.answer;
  if (accepted == null) {
    return {
      relevant_rows: {}, identifier: null, data_profile_id: null, drop: null,
      attributes: Object.fromEntries(Object.keys(record.attributes).map((name) => [name, null])),
    };
  }
  return {
    relevant_rows: { ...accepted.relevant_rows },
    identifier: withoutDecision(accepted.identifier),
    data_profile_id: withoutDecision(accepted.data_profile_id),
    attributes: Object.fromEntries(
      Object.entries(accepted.attributes).map(([name, slot]) => [name, withoutDecision(slot)]),
    ),
    drop: withoutDecision(accepted.drop),
  };
}

// The accepted groups of an evidence_changed prompt whose saved snapshots no
// longer match the rows they were taken from, or cannot be verified at all
// (Spec 3 section 2.3; ingestion/walk/evidence.py): [{ group, slot, saved,
// current, unverified }], `slot` the group's identifier, profile or drop slot,
// `saved` the snapshots it is bound to, `current` the prompt's snapshots of the
// same rows as they read now. A group is unverified when it is bound to no
// snapshot, to a key with none, or not to its own subject and selected source.
export function affectedGroups(prompt) {
  const answer = prompt.answer;
  // The key a current snapshot answers a saved one by: an incoming row by its
  // six provenance fields; a belief by its original identifier alone, whatever
  // profile holds it now, as the backend looks a belief up, so the same number
  // now owned by another profile reads as a changed row, not a missing one.
  const sourceKey = (ref) => canonicalJson(Object.hasOwn(ref, 'source') ? { belief: ref.source.identifier } : ref);
  const current = new Map(prompt.relevant_rows.map((row) => [sourceKey(row.ref), row]));
  const own = canonicalJson(ownReference(prompt.subject));
  const affected = [];
  for (const group of SLOT_GROUPS) {
    const slot = answer[group];
    if (slot === null) continue;
    const keys = slot.basis.evidence_keys;
    const saved = keys.map((key) => answer.relevant_rows[key]).filter((row) => row !== undefined);
    const bound = saved.map((row) => canonicalJson(row.ref));
    const required = [own];
    if (slot.reference !== null) required.push(canonicalJson(ownReference(subjectOfReference(slot.reference))));
    const unverified = keys.length === 0 || saved.length !== keys.length || required.some((ref) => !bound.includes(ref));
    const changed = saved.some((row) => {
      const now = current.get(sourceKey(row.ref));
      return now !== undefined && canonicalJson(now) !== canonicalJson(row);
    });
    if (!unverified && !changed) continue;
    const shown = saved.map((row) => current.get(sourceKey(row.ref))).filter((row) => row !== undefined);
    const now = unverified && shown.length === 0 ? [...current.values()] : shown;
    affected.push({ group, slot, saved, current: now, unverified });
  }
  return affected;
}

// ── Resolving a choice ─────────────────────────────────────────────────────

// The slot groups one choice on `prompt` (a question record) assigns or
// withdraws, on every subject it affects (Spec 3 sections 2.1 and 7.1):
//
//   [{ questionId, group, slots, evidence }]
//
// `group` is 'identifier', 'data_profile_id' (the profile and every attribute,
// one bundle) or 'drop'; `slots` the group's new slots ({ identifier },
// { data_profile_id, attributes } or { drop }), or null to withdraw it;
// `evidence` the snapshots the new slots are bound to, by key.
//
// `selection` holds what the choice needs:
//   identifier  the typed identifier (typed; revise of an identifier without `values`)
//   source      the record copied from (join, pull, move_there, both_right; revise
//               of a profile bundle, which moves the subject there)
//   targets     the records moved here (move_here)
//   carried     the carried rows given the same answer (typed, join, move_there, exclude)
//   group       the saved group revised (reconfirm, revise, keep_literal, withdraw)
//   values      what a rewritten dependent group takes from its changed source (revise)
//   owner       the source that now holds a changed belief's identifier (pull and
//               move_here on an evidence_changed prompt)
// `roster` is every record of the batch, by id.
export async function resolveChoice(prompt, choice, selection, roster) {
  const bySubject = recordsBySubject(roster);
  const changes = [];
  // One change: `slots` on `target`'s group, bound to the snapshots `rows`.
  const addChange = async (target, group, slots, rows) => {
    const evidence = {};
    const keys = [];
    for (const row of rows) {
      if (row === null) throw new Error(`A snapshot this choice needs is missing from question ${target.id}.`);
      const key = await evidenceKey(row);
      evidence[key] = row;
      if (!keys.includes(key)) keys.push(key);
    }
    const bound = Object.hasOwn(slots, 'attributes')
      ? [slots.data_profile_id, ...Object.values(slots.attributes)]
      : Object.values(slots);
    for (const slot of bound) slot.basis.evidence_keys = keys;
    changes.push({ questionId: target.id, group, slots, evidence });
  };
  const withdrawal = (target, group) => changes.push({ questionId: target.id, group, slots: null, evidence: {} });
  // A slot this choice on `prompt` decides: `value`, shown before as `from`,
  // recorded under the choice made unless `recordedChoice` names another.
  const slotOf = (value, from, {
    trigger, reference = null, comparison = null, includedVia = [], recordedChoice = choice,
  }) => ({
    value,
    basis: { question_id: prompt.id, trigger, choice: recordedChoice, from, comparison, evidence_keys: [] },
    reference,
    included_via: includedVia,
  });
  const identifierSlot = (target, value, options) => ({
    ...slotOf(value, target.identifier, options), confirmed_profile: options.confirmedProfile,
  });
  // A profile with every attribute of the target's table, sharing one basis.
  const bundle = (target, profile, attributes, options) => ({
    data_profile_id: slotOf(profile, target.data_profile_id, options),
    attributes: Object.fromEntries(Object.keys(target.attributes).map((name) => [
      name, slotOf(attributes[name], target.attributes[name], options),
    ])),
  });
  const typedIdentifier = () => {
    const typed = (selection.identifier || '').trim();
    if (typed === '') throw new GestureRefused(TYPE_AN_IDENTIFIER);
    if (typed.startsWith(PLACEHOLDER_PREFIX)) throw new GestureRefused(NO_PLACEHOLDER);
    return typed;
  };
  const recordOf = (subject) => {
    const record = bySubject.get(canonicalJson(subject));
    if (record === undefined) throw new Error('A record this choice needs is not among the batch\'s questions.');
    return record;
  };
  // A record's snapshot as the prompt shows it now, else its own record's.
  const currentEvidence = (record) => prompt.relevant_rows.find(
    (row) => canonicalJson(row.ref) === canonicalJson(ownReference(record.subject)),
  ) || ownEvidence(record);
  // The incoming row a choice on a row's prompt is decided for, and that
  // row's own confirmation of its identifier and profile ('own'), which a pull
  // or a move here also saves beside the change it makes to the other record.
  const promptTrigger = prompt.subject.kind === 'row' ? ownReference(prompt.subject) : null;
  const confirmPrompt = () => addChange(prompt, 'identifier', {
    identifier: identifierSlot(prompt, prompt.identifier, {
      trigger: promptTrigger, confirmedProfile: prompt.data_profile_id, recordedChoice: 'own',
    }),
  }, [ownEvidence(prompt)]);
  const carried = selection.carried || [];

  if (choice === 'own') {
    await confirmPrompt();
  } else if (choice === 'typed' || choice === 'join' || choice === 'both_right') {
    // The row filed under a typed identifier or a listed look-alike's, the
    // carried rows with it; or kept under its own beside a selected comparison.
    const source = choice === 'typed' ? null : selection.source;
    let value = prompt.identifier;
    if (choice === 'typed') value = typedIdentifier();
    if (choice === 'join') value = source.identifier;
    for (const target of choice === 'both_right' ? [prompt] : [prompt, ...carried]) {
      const options = {
        trigger: promptTrigger, confirmedProfile: prompt.data_profile_id,
        reference: source === null ? null : valueReference(source),
        comparison: source === null ? null : comparisonOf(source),
        includedVia: target === prompt ? [] : inclusionOf(target, prompt.identifier),
      };
      const rows = source === null ? [ownEvidence(target)] : [ownEvidence(target), ownEvidence(source)];
      await addChange(target, 'identifier', { identifier: identifierSlot(target, value, options) }, rows);
    }
  } else if (choice === 'move_there') {
    // The row, and the carried rows, keep the identifier and move to the
    // comparison's profile with the selected source's attributes.
    const source = selection.source;
    for (const target of [prompt, ...carried]) {
      const options = {
        trigger: promptTrigger, reference: valueReference(source), comparison: comparisonOf(source),
        confirmedProfile: source.data_profile_id,
        includedVia: target === prompt ? [] : inclusionOf(target, prompt.identifier),
      };
      const rows = [ownEvidence(target), ownEvidence(source)];
      await addChange(target, 'identifier', { identifier: identifierSlot(target, prompt.identifier, options) }, rows);
      const moved = bundle(target, source.data_profile_id, source.attributes, options);
      await addChange(target, 'data_profile_id', moved, rows);
    }
  } else if (choice === 'exclude') {
    // The row, and the carried rows, left out. Leaving a row out replaces its
    // other assignments, which would contradict it.
    for (const target of [prompt, ...carried]) {
      await addChange(target, 'drop', { drop: slotOf(true, false, { trigger: promptTrigger }) }, [ownEvidence(target)]);
      withdrawal(target, 'identifier');
      withdrawal(target, 'data_profile_id');
    }
  } else if (choice === 'withdraw') {
    withdrawal(prompt, selection.group);
  } else if ((choice === 'pull' || choice === 'move_here') && prompt.kind === 'evidence_changed') {
    // A belief whose identifier another profile now holds: its saved
    // identifier is withdrawn and given to the source that holds it now
    // (Spec 3 section 2.3), copied from the incoming row that authorised the
    // saved one; a move here also moves that source to the row's profile.
    const saved = prompt.answer.identifier;
    const owner = selection.owner;
    const trigger = recordOf({ kind: 'row', ...saved.basis.trigger });
    const options = {
      trigger: saved.basis.trigger, reference: valueReference(trigger), comparison: comparisonOf(trigger),
      confirmedProfile: choice === 'pull' ? owner.data_profile_id : trigger.data_profile_id,
    };
    const rows = [currentEvidence(owner), ownEvidence(trigger)];
    withdrawal(prompt, 'identifier');
    await addChange(owner, 'identifier', { identifier: identifierSlot(owner, saved.value, options) }, rows);
    if (choice === 'move_here') {
      const moved = bundle(owner, trigger.data_profile_id, trigger.attributes, options);
      await addChange(owner, 'data_profile_id', moved, rows);
    }
  } else if (choice === 'pull') {
    // The row's identifier is right and the selected look-alike is the same:
    // the look-alike takes the row's identifier, the row confirms its own.
    const source = selection.source;
    await confirmPrompt();
    await addChange(source, 'identifier', {
      identifier: identifierSlot(source, prompt.identifier, {
        trigger: promptTrigger, reference: valueReference(prompt), comparison: comparisonOf(prompt),
        confirmedProfile: source.data_profile_id,
      }),
    }, [ownEvidence(source), ownEvidence(prompt)]);
  } else if (choice === 'move_here') {
    // The row keeps its identifier and profile; each chosen comparison moves
    // to the row's profile with its attributes, each on its own record.
    await confirmPrompt();
    for (const target of selection.targets) {
      await addChange(target, 'data_profile_id', bundle(target, prompt.data_profile_id, prompt.attributes, {
        trigger: promptTrigger, reference: valueReference(prompt), comparison: comparisonOf(prompt),
        includedVia: inclusionOf(target, prompt.compared_with.identifier),
      }), [ownEvidence(target), ownEvidence(prompt)]);
    }
  } else if (choice === 'revise' && selection.group === 'data_profile_id' && selection.source) {
    // A saved profile bundle revised through a move there (Spec 3 sections 2.3
    // and 7.2): the subject takes the selected source's profile and
    // attributes, copied from it and bound to both rows as they read now.
    const source = selection.source;
    const saved = prompt.answer.data_profile_id;
    const moved = bundle(prompt, source.data_profile_id, source.attributes, {
      trigger: saved.basis.trigger, reference: valueReference(source), comparison: comparisonOf(source),
    });
    await addChange(prompt, 'data_profile_id', moved, [ownEvidence(prompt), currentEvidence(source)]);
  } else if (choice === 'reconfirm' || choice === 'revise' || choice === 'keep_literal') {
    // A saved group decided again: reconfirmed as it stands against the rows
    // as they read now; revised to a typed identifier, or rewritten to what its
    // changed source now reads (`values`); or kept as a literal no longer
    // copied from its source.
    const group = selection.group;
    const saved = prompt.answer[group];
    const rewrite = selection.values || null;
    const detached = choice === 'keep_literal' || (choice === 'revise' && rewrite === null);
    const options = {
      trigger: saved.basis.trigger,
      reference: detached ? null : saved.reference,
      includedVia: detached ? [] : saved.included_via,
      confirmedProfile: detached ? prompt.data_profile_id : saved.confirmed_profile,
    };
    const rows = [ownEvidence(prompt)];
    if (options.reference !== null) {
      const source = recordOf(subjectOfReference(options.reference));
      rows.push(currentEvidence(source));
      options.comparison = { ...comparisonOf(source), ...(rewrite || {}) };
    }
    if (group === 'identifier') {
      let value = saved.value;
      if (rewrite !== null) value = rewrite.identifier;
      else if (choice === 'revise') value = typedIdentifier();
      await addChange(prompt, 'identifier', { identifier: identifierSlot(prompt, value, options) }, rows);
    } else if (group === 'drop') {
      await addChange(prompt, 'drop', { drop: slotOf(saved.value, false, options) }, rows);
    } else {
      const savedAttributes = Object.fromEntries(
        Object.entries(prompt.answer.attributes).map(([name, slot]) => [name, slot.value]),
      );
      const profile = rewrite === null ? saved.value : rewrite.data_profile_id;
      const attributes = rewrite === null ? savedAttributes : rewrite.attributes;
      await addChange(prompt, 'data_profile_id', bundle(prompt, profile, attributes, options), rows);
    }
  } else {
    throw new Error(`'${choice}' is not a choice a question offers.`);
  }
  return changes;
}

// ── Merging the changes into drafts ────────────────────────────────────────

// The complete replacement answer of every subject the changes reach, built
// on its accepted answer, by question id; and the conflicts: a subject's
// group that two changes set differently, which the person must make agree
// before submitting (no order or time decides between them).
export function draftAnswers(changes, roster) {
  const byGroup = new Map();
  const conflicts = [];
  for (const change of changes) {
    const key = `${change.questionId}\u0000${change.group}`;
    const existing = byGroup.get(key);
    if (existing === undefined) {
      byGroup.set(key, change);
    } else if (stableStringify(existing.slots) !== stableStringify(change.slots)
        && !conflicts.some((conflict) => conflict.questionId === change.questionId && conflict.group === change.group)) {
      conflicts.push({ questionId: change.questionId, group: change.group });
    }
  }
  const drafts = new Map();
  for (const change of byGroup.values()) {
    if (!drafts.has(change.questionId)) drafts.set(change.questionId, replacementAnswer(roster.get(change.questionId)));
    const draft = drafts.get(change.questionId);
    if (change.group === 'data_profile_id') {
      draft.data_profile_id = change.slots === null ? null : change.slots.data_profile_id;
      draft.attributes = change.slots === null
        ? Object.fromEntries(Object.keys(draft.attributes).map((name) => [name, null]))
        : change.slots.attributes;
    } else {
      draft[change.group] = change.slots === null ? null : change.slots[change.group];
    }
    Object.assign(draft.relevant_rows, change.evidence);
  }
  // Each draft keeps exactly the snapshots its slots are bound to.
  for (const draft of drafts.values()) {
    const used = new Set();
    for (const slot of [draft.identifier, draft.data_profile_id, draft.drop, ...Object.values(draft.attributes)]) {
      if (slot !== null) slot.basis.evidence_keys.forEach((key) => used.add(key));
    }
    draft.relevant_rows = Object.fromEntries(Object.entries(draft.relevant_rows).filter(([key]) => used.has(key)));
  }
  return { drafts, conflicts };
}

// The assignments the drafts disturb (Spec 3 sections 2.1 and 7.1), read over
// the complete proposed map: every subject's accepted slots, overlaid with its
// draft, so a slot a draft creates is judged as a saved one is. A slot is
// disturbed when it is an identifier or profile bundle that copied its value
// from a subject whose matching field the drafts change to another value, or
// that was included through another slot the drafts change. Each needs the
// person's decision before a submission: rewrite it to what its source reads
// now ('revise', when it copied a value and no inclusion ties it), keep it as
// a literal no longer tied to the source ('keep_literal'), or withdraw it.
//
//   [{ questionId, group, slot, sourceId, rewriteTo, reasons: [{ questionId, field, kind }] }]
//
// `slot` is the proposed slot; `sourceId` the source a copied value came
// from; `rewriteTo` that source's new values when the slot can be rewritten to
// them, else null (an inclusion is kept or withdrawn, never rewritten).
export function dependentSlots(drafts, roster) {
  // The identifier, and the profile with its attributes, each draft gives its
  // subject where they differ from what its record shows now, by
  // '<qid>\u0000identifier' and '<qid>\u0000data_profile_id'. A withdrawn
  // group gives back the subject's own underlying values (ownSnapshot).
  const changed = new Map();
  for (const [questionId, draft] of drafts) {
    const record = roster.get(questionId);
    const accepted = record.answer || {};
    const oldIdentifier = accepted.identifier != null ? accepted.identifier.value : record.identifier;
    let newIdentifier = oldIdentifier;
    if (draft.identifier !== null) newIdentifier = draft.identifier.value;
    else if (accepted.identifier != null) newIdentifier = ownSnapshot(record, accepted.identifier).identifier;
    if (newIdentifier !== oldIdentifier) changed.set(`${questionId}\u0000identifier`, { identifier: newIdentifier });
    const oldProfile = accepted.data_profile_id != null ? accepted.data_profile_id.value : record.data_profile_id;
    let newProfile = oldProfile;
    let newAttributes = record.attributes;
    if (draft.data_profile_id !== null) {
      newProfile = draft.data_profile_id.value;
      newAttributes = Object.fromEntries(Object.entries(draft.attributes).map(([name, slot]) => [name, slot.value]));
    } else if (accepted.data_profile_id != null) {
      const baseline = ownSnapshot(record, accepted.data_profile_id);
      newProfile = baseline.data_profile_id;
      newAttributes = baseline.attributes;
    }
    if (newProfile !== oldProfile || canonicalJson(newAttributes) !== canonicalJson(record.attributes)) {
      changed.set(`${questionId}\u0000data_profile_id`, { data_profile_id: newProfile, attributes: newAttributes });
    }
  }
  const bySubject = recordsBySubject(roster);
  const dependents = [];
  for (const record of roster.values()) {
    const proposed = drafts.has(record.id) ? drafts.get(record.id) : record.answer;
    if (proposed == null) continue;
    for (const group of ['identifier', 'data_profile_id']) {
      if (proposed[group] === null) continue;
      const slot = withoutDecision(proposed[group]);
      const reasons = [];
      let sourceId = null;
      let sourceNow = null;
      if (slot.reference !== null) {
        const source = bySubject.get(canonicalJson(subjectOfReference(slot.reference)));
        const sourceChange = source === undefined ? undefined : changed.get(`${source.id}\u0000${group}`);
        // What the slot copied, in the shape of its source's new values: a
        // copy already rewritten to them is decided.
        let copied = { identifier: slot.value };
        if (group === 'data_profile_id') {
          const attributes = Object.fromEntries(
            Object.entries(proposed.attributes).map(([name, attribute]) => [name, attribute.value]),
          );
          copied = { data_profile_id: slot.value, attributes };
        }
        if (sourceChange !== undefined && canonicalJson(copied) !== canonicalJson(sourceChange)) {
          reasons.push({ questionId: source.id, field: group, kind: 'reference' });
          sourceId = source.id;
          sourceNow = sourceChange;
        }
      }
      for (const item of slot.included_via) {
        // A slot included through itself is replaced by its own draft, not disturbed by it.
        const itself = item.question_id === record.id && item.field === group;
        if (!itself && changed.has(`${item.question_id}\u0000${item.field}`)) {
          reasons.push({ questionId: item.question_id, field: item.field, kind: 'inclusion' });
        }
      }
      if (reasons.length === 0) continue;
      const rewriteTo = reasons.every((reason) => reason.kind === 'reference') ? sourceNow : null;
      dependents.push({ questionId: record.id, group, slot, sourceId, rewriteTo, reasons });
    }
  }
  return dependents;
}

// ── Submitting ─────────────────────────────────────────────────────────────

// One submission attempt: the drafts (by question id) and the records they
// were made from, fixed for its life. submitAnswers records how far it got, so
// a retry of the same attempt carries on from there:
//   claimed         the batch is reserved under `id`
//   chunks          the question ids of each staging transaction, once checked
//   staged          the question ids whose chunk acknowledged
//   stagingStarted  a staging write was sent: only the backend may discard it now
export function newSubmission(drafts, frozen) {
  return {
    id: crypto.randomUUID().replace(/-/g, ''), drafts, frozen,
    claimed: false, chunks: null, staged: new Set(), stagingStarted: false,
  };
}

// Submits an attempt's drafts (Spec 3 section 7.1):
//
//   1. reserve the parked batch for this attempt (submitting: { id,
//      claimed_at, by, staged: false });
//   2. before staging anything, under the reservation, read every question
//      again: each record the drafts were made from, or take evidence from,
//      must read as it did; no saved answer may newly depend on a change; each
//      staged and promoted answer must fit its document. A profile is taken as
//      the records name it, a tombstoned one included (00 B.12: its id never
//      stops resolving; the backend validates it). Any of these failing
//      releases the reservation at once (waking a member whose deletion waited
//      on it) and throws;
//   3. stage the drafts in pending_answer, in transactions of at most
//      ANSWER_WRITE_CHUNK_SIZE questions and COMMIT_BYTES_LIMIT bytes, each
//      refreshing the reservation and setting it staged;
//   4. accept: the reservation cleared, and the batch and every member waiting.
//
// Answers 'accepted', or 'awaiting_cleanup' when staging started but the
// attempt can no longer finish (its reservation was taken over or went stale,
// or the batch moved on): what it staged is the reaper's to discard, and the
// drafts can be submitted again once the batch is free. Throws a GestureRefused
// when the attempt was refused before staging (its reservation released), and
// any other error as it is; while the reservation is still this attempt's, the
// same attempt can be retried.
export async function submitAnswers(companyId, tableId, batchId, submission, { onProgress = () => {} } = {}) {
  const user = platformAuth.currentUser;
  if (!user) throw new GestureRefused(SIGNED_OUT);
  const uid = user.uid;
  const scope = tableScope(companyId, tableId);
  const batchRef = doc(db, `${scope}/batches/${batchId}`);
  const questionRef = (questionId) => doc(db, `${scope}/batches/${batchId}/questions/${questionId}`);
  const fileRef = (fileId) => doc(db, `${scope}/files/${fileId}`);
  // Whether the batch, as read, is still reserved by this attempt and the reservation is live.
  const ownsLiveReservation = (batch) => batch !== null && batch.status === 'parked' && batch.submitting != null
    && batch.submitting.id === submission.id && batch.submitting.by === uid
    && Date.now() - batch.submitting.claimed_at.toMillis() < CLAIM_STALE_SECONDS * 1000;
  // A question's data as accepted: everything but its staging.
  const acceptedView = (record) => {
    const { id, pending_answer: staging, ...accepted } = record;
    return stableStringify(accepted);
  };

  // 1. The reservation.
  if (!submission.claimed) {
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(batchRef);
      if (!snapshot.exists()) throw new GestureRefused(CHECK_GONE);
      const batch = snapshot.data();
      // A retry after a lost acknowledgement finds the reservation already made.
      if (batch.submitting != null && batch.submitting.id === submission.id && batch.submitting.by === uid) return;
      if (batch.status !== 'parked') throw new GestureRefused(NOT_TAKING_ANSWERS);
      if (batch.submitting != null) throw new GestureRefused(ANOTHER_SUBMISSION);
      transaction.update(batchRef, {
        submitting: { id: submission.id, claimed_at: serverTimestamp(), by: uid, staged: false },
      });
    });
    submission.claimed = true;
  }

  // 2. The checks before staging, under the reservation.
  if (submission.chunks === null) {
    try {
      const current = await loadRoster(companyId, tableId, batchId);
      const frozenBySubject = recordsBySubject(submission.frozen);
      const required = new Set(submission.drafts.keys());
      let changedAnswers = 0;
      for (const [questionId, draft] of submission.drafts) {
        const accepted = replacementAnswer(submission.frozen.get(questionId));
        if (stableStringify(draft) !== stableStringify(accepted)) changedAnswers += 1;
        for (const group of SLOT_GROUPS) {
          if (draft[group] === null || stableStringify(draft[group]) === stableStringify(accepted[group])) continue;
          const slot = draft[group];
          required.add(slot.basis.question_id);
          for (const key of slot.basis.evidence_keys) {
            const source = frozenBySubject.get(canonicalJson(subjectOfReference(draft.relevant_rows[key].ref)));
            if (source === undefined) throw new GestureRefused(QUESTION_CHANGED);
            required.add(source.id);
          }
          slot.included_via.forEach((item) => required.add(item.question_id));
        }
      }
      for (const questionId of required) {
        const frozen = submission.frozen.get(questionId);
        const now = current.get(questionId);
        if (frozen === undefined || now === undefined || acceptedView(frozen) !== acceptedView(now)) {
          throw new GestureRefused(QUESTION_CHANGED);
        }
      }
      if (changedAnswers === 0) throw new GestureRefused(NOTHING_CHANGED);
      // The accepted answers as they read now: a submission completed meanwhile
      // can have made a saved answer depend on one of these changes.
      if (dependentSlots(submission.drafts, current).length > 0) throw new GestureRefused(DEPENDENT_CHANGED);
      // Each document as staged, and as promotion will leave it with its
      // decision metadata, must fit; the staged ones fill the chunks.
      const decidedAt = new Date();
      const chunks = [];
      let chunk = [];
      let chunkBytes = 16384;
      for (const [questionId, draft] of submission.drafts) {
        const { id, ...frozen } = submission.frozen.get(questionId);
        const pending = { submission_id: submission.id, answer: draft, decided_by: uid, decided_at: decidedAt };
        // The UTF-8 bytes of the document as JSON, the measure the cleaning
        // gestures budget a document with.
        const stagedBytes = new TextEncoder().encode(JSON.stringify({ ...frozen, pending_answer: pending })).length;
        const decision = { submission_id: submission.id, decided_by: uid, decided_at: decidedAt };
        const promoted = JSON.parse(JSON.stringify(draft));
        const promotedSlots = [promoted.identifier, promoted.data_profile_id, promoted.drop];
        for (const slot of [...promotedSlots, ...Object.values(promoted.attributes)]) {
          if (slot !== null) slot.decision = decision;
        }
        const promotedBytes = new TextEncoder().encode(JSON.stringify({
          ...frozen, answer: promoted, submission_id: submission.id, decided_by: uid, decided_at: decidedAt,
          open_at: null, pending_answer: null,
        })).length;
        if (Math.max(stagedBytes, promotedBytes) > DOCUMENT_BYTES_LIMIT) throw new GestureRefused(ANSWER_TOO_LARGE);
        const full = chunk.length === ANSWER_WRITE_CHUNK_SIZE || chunkBytes + stagedBytes + 1024 > COMMIT_BYTES_LIMIT;
        if (chunk.length > 0 && full) {
          chunks.push(chunk);
          chunk = [];
          chunkBytes = 16384;
        }
        chunk.push(questionId);
        chunkBytes += stagedBytes + 1024;
      }
      chunks.push(chunk);
      submission.chunks = chunks;
    } catch (failure) {
      // Nothing was staged, so this attempt releases its own reservation at
      // once, waking a member whose deletion request the reservation held back.
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(batchRef);
        const batch = snapshot.exists() ? snapshot.data() : null;
        if (batch === null || batch.status !== 'parked' || batch.submitting == null
            || batch.submitting.id !== submission.id || batch.submitting.by !== uid || batch.submitting.staged) {
          return;
        }
        let waiting = null;
        for (const member of batch.files) {
          const file = await transaction.get(fileRef(member));
          if (waiting === null && file.exists() && file.data().deletion != null && file.data().deletion.failed == null) {
            waiting = member;
          }
        }
        transaction.update(batchRef, { submitting: null });
        if (waiting !== null) transaction.update(fileRef(waiting), { woken_at: serverTimestamp() });
      });
      submission.claimed = false;
      throw failure;
    }
  }

  // 3. Staging.
  const total = submission.drafts.size;
  onProgress(submission.staged.size, total);
  for (const chunk of submission.chunks) {
    if (chunk.every((questionId) => submission.staged.has(questionId))) continue;
    // Set before the write is sent: an uncertain chunk may have committed, so
    // from here on only the backend may discard this attempt's staging.
    submission.stagingStarted = true;
    const outcome = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(batchRef);
      if (!ownsLiveReservation(snapshot.exists() ? snapshot.data() : null)) return 'lost';
      const questions = [];
      for (const questionId of chunk) questions.push(await transaction.get(questionRef(questionId)));
      // Every question of the chunk is checked before any is written: a
      // transaction that returns commits whatever it queued, and the rules
      // refuse a question's staging unless the reservation's refresh is in
      // the same commit.
      const toStage = [];
      for (const question of questions) {
        if (!question.exists()) return 'lost';
        const record = { id: question.id, ...question.data() };
        if (acceptedView(record) !== acceptedView(submission.frozen.get(question.id))) return 'lost';
        const staged = record.pending_answer;
        if (staged != null) {
          // Staged already by this attempt, as it is now: kept, with its time.
          const same = staged.submission_id === submission.id && staged.decided_by === uid
            && stableStringify(staged.answer) === stableStringify(submission.drafts.get(question.id));
          if (!same) return 'lost';
          continue;
        }
        toStage.push(question);
      }
      for (const question of toStage) {
        transaction.update(question.ref, {
          pending_answer: {
            submission_id: submission.id, answer: submission.drafts.get(question.id), decided_by: uid,
            decided_at: serverTimestamp(),
          },
        });
      }
      transaction.update(batchRef, { 'submitting.staged': true, 'submitting.claimed_at': serverTimestamp() });
      return 'staged';
    });
    if (outcome === 'lost') return 'awaiting_cleanup';
    chunk.forEach((questionId) => submission.staged.add(questionId));
    onProgress(submission.staged.size, total);
  }

  // 4. Acceptance.
  try {
    const outcome = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(batchRef);
      const batch = snapshot.exists() ? snapshot.data() : null;
      if (!ownsLiveReservation(batch) || !batch.submitting.staged) return 'lost';
      transaction.update(batchRef, { submitting: null, status: 'waiting', waiting_since: serverTimestamp() });
      for (const member of batch.files) {
        transaction.update(fileRef(member), { batch_status: 'waiting', status: 'waiting' });
      }
      return 'accepted';
    });
    if (outcome === 'accepted') return 'accepted';
  } catch (failure) {
    // An uncertain finish. Still this attempt's reservation: nothing was
    // accepted, and the person may try the same attempt again. Otherwise the
    // batch left this attempt's hands, judged below.
    const snapshot = await getDoc(batchRef);
    const batch = snapshot.exists() ? snapshot.data() : null;
    if (ownsLiveReservation(batch)) throw failure;
  }
  // The batch left this attempt's hands. A batch that reads waiting, running
  // or completed may have been moved on by another person's submission after
  // the reaper discarded this one, so only this attempt's own id says its
  // answers were accepted. The first drafted question that still exists says
  // which (a walk deletes the records of a file that left the batch): its
  // answer promoted under this attempt's id; or still staged under it in a
  // batch that is waiting, running or completed, which only this attempt's
  // acceptance can have moved on, since the reaper discards a stale
  // attempt's staging before it frees the batch.
  for (const questionId of submission.drafts.keys()) {
    const question = await getDoc(questionRef(questionId));
    if (!question.exists()) continue;
    const record = question.data();
    if (record.submission_id === submission.id) return 'accepted';
    if (record.pending_answer == null || record.pending_answer.submission_id !== submission.id) {
      return 'awaiting_cleanup';
    }
    const snapshot = await getDoc(batchRef);
    if (snapshot.exists() && ['waiting', 'running', 'completed'].includes(snapshot.data().status)) return 'accepted';
    return 'awaiting_cleanup';
  }
  return 'awaiting_cleanup';
}
